import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatAutocompleteTrigger, MatAutocompleteSelectedEvent, MatAutocomplete } from '@angular/material/autocomplete';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { parseToStateDistrict } from '../../helpers/helper-functions';
import { DataType } from '../../models/lookups';
import { ScoreCard } from '../../models/score-card.enum';
import { setSelectedCongressman, setCongressionalDistrict, setSelectedLocalUnion, getCongressionalDistrict } from '../../store/core.actions';
import { selectCongressmenDisplayNames, selectStateMap } from '../../store/core.selector';
import { AppState } from '../../store/core.state';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatSuffix, MatHint } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatDivider } from '@angular/material/divider';

@Component({
    selector: 'app-home-page',
    templateUrl: './home-page.component.html',
    styleUrls: ['./home-page.component.scss'],
    imports: [MatDivider, FormsModule, MatFormField, MatLabel, MatInput, MatTooltip, MatButton, MatHint,
        MatAutocompleteTrigger, MatIcon, MatSuffix, MatAutocomplete, MatOption]
})

export class HomePageComponent implements OnInit, OnDestroy {
  unsubscribe$: Subject<void> = new Subject();
  @ViewChild(MatAutocompleteTrigger)
  trigger!: MatAutocompleteTrigger;
  congressmanSearchValue: string;
  isResetVisible: boolean = false;
  congressmanDisplayNames: DataType[];
  searchValue: string;
  selectedCongressman: DataType;
  filteredCongressmen: DataType[] = [];
  stateMap: Map<string, number>;


  constructor(private store: Store<AppState>, private router: Router) { }

  ngOnInit() {
    this.store.pipe(select(selectCongressmenDisplayNames), takeUntil(this.unsubscribe$))
      .subscribe(congressmanDisplayNames => {
        this.congressmanDisplayNames = congressmanDisplayNames;
        this.filteredCongressmen = congressmanDisplayNames;
      });
    this.store.pipe(select(selectStateMap), takeUntil(this.unsubscribe$)).subscribe(stateMap => this.stateMap = stateMap);
  }

  onCongressmanSelected = (event: MatAutocompleteSelectedEvent) => this.selectedCongressman = event.option.value;

  onGetScorecard(){
    this.store.dispatch(setSelectedCongressman({ congressman: this.selectedCongressman}));
    this.router.navigate(['/scorecard'], { state: { scoreCardType: ScoreCard.SingleCongressman } });
  }

  onSearchChange(value: string) {
    const filterValue = value.toLowerCase();
    this.filteredCongressmen = this.congressmanDisplayNames.filter(c =>
      c.description.toLowerCase().includes(filterValue)
    );
  }

  onSubmit = () => {
    const value = this.searchValue.trim();

    const isCongressionalDistrict = /^[a-zA-Z]{2}-\d+$/.test(value);

    // Strip common LU prefixes (case-insensitive), then check for 2-4 digits
    // Matches: "IBEW LU 123", "IBEW Local 123", "Local 123", "LU 123", "Local Union 123", or just "123"
    const localUnionMatch = value.match(/^(?:ibew\s+)?(?:local\s+union|local|lu)?\s*(\d{1,4})$/i);
    const isLocalUnion = localUnionMatch !== null;

    if (isCongressionalDistrict) {
      let { stateId, district } = parseToStateDistrict(value, this.stateMap);
      this.store.dispatch(setCongressionalDistrict({ stateId: stateId ?? 0, districtNumber: district }));
      this.router.navigate(['/scorecard'], { state: { scoreCardType: ScoreCard.SingleCongressionalDistrict } });
    }
    else if (isLocalUnion) {
      const luNumber = Number(localUnionMatch![1]);
      this.store.dispatch(setSelectedLocalUnion({ selectedLocalUnion: luNumber }));
      this.router.navigate(['/scorecard'], { state: { scoreCardType: ScoreCard.MultiCongressionalDistrict } });
    }
    else {
      this.store.dispatch(getCongressionalDistrict({ address: value }));
      this.router.navigate(['/scorecard'], { state: { scoreCardType: ScoreCard.SingleCongressionalDistrict } });
    }

    this.isResetVisible = true;
  }

  displayCongressman = (c?: DataType) => c?.description ?? '';

  onReset = () => {
    this.searchValue = '';
    this.isResetVisible = false;
    this.congressmanSearchValue = '';
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
